import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api.js';
import { Layout } from '../components/Layout.js';
import { Button } from '../components/ui/Button.js';
import { Icon } from '../components/ui/Icon.js';

interface RewardsSummary {
  themes: Array<{
    key: string;
    name: string;
    unlockXp?: number;
    unlockCondition: string;
    unlocked: boolean;
    unlockedAt?: Date;
  }>;
  titles: Array<{
    key: string;
    name: string;
    unlockCondition: string;
    unlocked: boolean;
  }>;
  credits: {
    balance: number;
    totalEarned: number;
    totalRedeemed: number;
  };
  coupons: Array<{
    code: string;
    discountValue: string;
    earnedAt: Date;
    redeemedAt: Date | null;
  }>;
  shields: {
    count: number;
  };
  userProgress: {
    totalXp: number;
    level: number;
    weekStreak: number;
    totalPlans: number;
  };
}

export function RewardsPage() {
  const [redeemAmount, setRedeemAmount] = useState(500);
  const [copiedCode, setCopiedCode] = useState('');
  const qc = useQueryClient();

  const { data: rewards, isLoading } = useQuery<RewardsSummary>({
    queryKey: ['rewards-summary'],
    queryFn: () => api.get('/api/rewards/summary'),
  });

  const redeem = useMutation<any, Error, void>({
    mutationFn: async () => {
      return api.post('/api/credits/redeem', {
        creditAmount: redeemAmount,
        couponType: 'generic',
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rewards-summary'] });
      setRedeemAmount(500);
    },
  });

  const copyToClipboard = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(''), 2000);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="py-12 px-4 text-center">Loading rewards...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto py-8 px-4 space-y-12">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--ink)' }}>Rewards & Themes</h1>
          <p style={{ color: 'var(--ink-3)' }}>Earn achievements, unlock themes, and redeem credits</p>
        </div>

        {/* User Progress */}
        {rewards?.userProgress && (
          <div
            className="rounded-lg p-6 space-y-4"
            style={{ background: 'var(--surface-2)', border: '1px solid var(--line-soft)' }}
          >
            <h2 className="text-lg font-semibold" style={{ color: 'var(--ink)' }}>Your Progress</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p style={{ color: 'var(--ink-3)', fontSize: '0.875rem' }}>Level</p>
                <p className="text-2xl font-bold" style={{ color: 'var(--accent)' }}>{rewards.userProgress.level}</p>
              </div>
              <div>
                <p style={{ color: 'var(--ink-3)', fontSize: '0.875rem' }}>Total XP</p>
                <p className="text-2xl font-bold" style={{ color: 'var(--accent)' }}>{rewards.userProgress.totalXp}</p>
              </div>
              <div>
                <p style={{ color: 'var(--ink-3)', fontSize: '0.875rem' }}>Week Streak</p>
                <p className="text-2xl font-bold" style={{ color: 'var(--accent)' }}>{rewards.userProgress.weekStreak}</p>
              </div>
              <div>
                <p style={{ color: 'var(--ink-3)', fontSize: '0.875rem' }}>Plans Generated</p>
                <p className="text-2xl font-bold" style={{ color: 'var(--accent)' }}>{rewards.userProgress.totalPlans}</p>
              </div>
            </div>
          </div>
        )}

        {/* Themes Section */}
        {rewards?.themes && (
          <div>
            <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--ink)' }}>Themes ({rewards.themes.filter(t => t.unlocked).length}/{rewards.themes.length})</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {rewards.themes.map((theme) => (
                <div
                  key={theme.key}
                  className="rounded-lg p-4 cursor-pointer transition-all"
                  style={{
                    background: theme.unlocked ? 'var(--surface-2)' : 'var(--surface-3)',
                    border: `1px solid ${theme.unlocked ? 'var(--line-soft)' : 'var(--line)'}`,
                    opacity: theme.unlocked ? 1 : 0.5,
                  }}
                >
                  <p className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{theme.name}</p>
                  {!theme.unlocked && (
                    <p style={{ color: 'var(--ink-3)', fontSize: '0.75rem', marginTop: '0.5rem' }}>{theme.unlockCondition}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Titles Section */}
        {rewards?.titles && (
          <div>
            <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--ink)' }}>Cosmetic Titles ({rewards.titles.filter(t => t.unlocked).length}/{rewards.titles.length})</h2>
            <div className="space-y-2">
              {rewards.titles.map((title) => (
                <div
                  key={title.key}
                  className="rounded-lg p-3 flex items-center justify-between"
                  style={{
                    background: 'var(--surface-2)',
                    border: '1px solid var(--line-soft)',
                    opacity: title.unlocked ? 1 : 0.6,
                  }}
                >
                  <div>
                    <p className="font-semibold" style={{ color: title.unlocked ? 'var(--ink)' : 'var(--ink-3)' }}>{title.name}</p>
                    {!title.unlocked && (
                      <p style={{ color: 'var(--ink-4)', fontSize: '0.875rem' }}>Unlock: {title.unlockCondition}</p>
                    )}
                  </div>
                  {title.unlocked && (
                    <span style={{ color: 'var(--accent)', fontSize: '0.875rem' }}>✓ Unlocked</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Credits Section */}
        {rewards?.credits && (
          <div
            className="rounded-lg p-6"
            style={{ background: 'var(--surface-2)', border: '1px solid var(--line-soft)' }}
          >
            <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--ink)' }}>Credits</h2>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div>
                <p style={{ color: 'var(--ink-3)', fontSize: '0.875rem' }}>Balance</p>
                <p className="text-2xl font-bold" style={{ color: 'var(--accent)' }}>{rewards.credits.balance}</p>
              </div>
              <div>
                <p style={{ color: 'var(--ink-3)', fontSize: '0.875rem' }}>Total Earned</p>
                <p className="text-2xl font-bold" style={{ color: 'var(--accent)' }}>{rewards.credits.totalEarned}</p>
              </div>
              <div>
                <p style={{ color: 'var(--ink-3)', fontSize: '0.875rem' }}>Total Redeemed</p>
                <p className="text-2xl font-bold" style={{ color: 'var(--accent)' }}>{rewards.credits.totalRedeemed}</p>
              </div>
            </div>

            {rewards.credits.balance >= 500 && (
              <div className="space-y-3">
                <label style={{ color: 'var(--ink)' }} className="block">
                  Redeem amount:
                </label>
                <input
                  type="range"
                  min="500"
                  max="2500"
                  step="500"
                  value={redeemAmount}
                  onChange={(e) => setRedeemAmount(Number(e.target.value))}
                  className="w-full"
                />
                <p style={{ color: 'var(--ink-3)', fontSize: '0.875rem' }}>{redeemAmount} credits = ${(redeemAmount / 100).toFixed(2)}</p>
                <Button
                  onClick={() => redeem.mutate()}
                  disabled={redeem.isPending}
                  className="w-full"
                >
                  {redeem.isPending ? 'Redeeming...' : 'Redeem Coupon'}
                </Button>
                {redeem.isError && (
                  <p style={{ color: 'var(--danger)' }} className="text-sm">Error redeeming credits</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Coupons Section */}
        {rewards?.coupons && rewards.coupons.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--ink)' }}>Your Coupons</h2>
            <div className="space-y-3">
              {rewards.coupons.map((coupon, idx) => (
                <div
                  key={idx}
                  className="rounded-lg p-4 flex items-center justify-between"
                  style={{
                    background: coupon.redeemedAt ? 'var(--surface-3)' : 'var(--surface-2)',
                    border: '1px solid var(--line-soft)',
                  }}
                >
                  <div>
                    <p className="font-monospace font-bold" style={{ color: 'var(--ink)' }}>{coupon.code}</p>
                    <p style={{ color: 'var(--ink-3)', fontSize: '0.875rem' }}>
                      ${coupon.discountValue} {coupon.redeemedAt ? '• Redeemed' : '• Earned'}
                    </p>
                  </div>
                  {!coupon.redeemedAt && (
                    <button
                      onClick={() => copyToClipboard(coupon.code)}
                      style={{
                        background: 'var(--accent)',
                        color: 'white',
                        padding: '0.5rem 1rem',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      {copiedCode === coupon.code ? '✓ Copied' : 'Copy'}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Shields Section */}
        {rewards?.shields && (
          <div
            className="rounded-lg p-6"
            style={{ background: 'var(--surface-2)', border: '1px solid var(--line-soft)' }}
          >
            <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--ink)' }}>Streak Shields</h2>
            <p style={{ color: 'var(--ink-3)', fontSize: '0.875rem', marginBottom: '1rem' }}>Earned via cook streaks</p>
            <div className="text-4xl font-bold" style={{ color: 'var(--accent)' }}>
              🛡️ × {rewards.shields.count}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
